/**
 * THE COC FOLLOW-UPS WORK AREA (COC-EXPERIENCE-V1) over the merged #589
 * follow-up engine (COC-FOLLOWUP-CORE-JOURNEY-V1).
 *
 * SCOPE (#593 corrective item 3): the COC HOME is the five-tab command center
 * (docs/design/growth-os/mockups/03-coc-command-center.html: My Work /
 * Acceptance / Payments / Escalations / Clinic Health) and is UNBUILT. This
 * surface is ONE of those tabs -- the Follow-ups work area (the "Acceptance"
 * tab) -- seated as a nav DOOR, never the home; the COC lands on the interim
 * My clinics page.
 *
 * COMPOSITION (#593 FINAL visual corrective, 2026-08-20): the system ORGANIZES
 * the work; the human DECIDES. The page answers WHAT NEEDS ME NOW in a strong
 * ATTENTION region (the lawful actionable STATES -- overdue past the
 * first-follow-up SLA, CSD eligible -- as counts, never an invented priority
 * score), then a QUIETER full follow-up REGISTER: a deterministic, on-glass-
 * stated order (overdue first, then longest waiting) over a token-spaced,
 * column-aligned list (never a dense DataTable). Each register row leads with
 * WHO, then the FULL treatment scope (all names -- never "+1 more"), the clinic
 * / doctor, a quiet money estimate, the waiting / overdue state, what happened
 * last, and ONE action ("Follow up"). Depth lives OFF the register: "Follow up"
 * opens a focused contact DRAWER, never an exploded row.
 *
 * ONE CONTACT, MANY TREATMENTS (#589, RULING 1/6): in the drawer the COC picks
 * the exact treatment items discussed in one real call, sets each item's
 * outcome (Still open / Declined / Unreachable / Cold), gives a Cost/Other
 * reason ONLY under a decline (RULING 3), and SAVES ONCE. CONVERTED is never
 * here (payment-derived, RULING 2). On reload each item shows its persisted
 * state with the supervisor-safe named actor + time (RULING 7/8) -- no raw
 * actor principal, no clinical narrative, no machine ids on the glass.
 *
 * PERSISTENCE (LIVE): the contact + per-item disposition carrier (migration
 * 20260820_1) is FOUNDER-APPLIED (applied-state ledger, 2026-08-20): a Save
 * persists one contact with per-item outcomes, attributed to the authenticated
 * COC. MOCK mode serves a seeded read fixture; a deployed FULL-mode walk proves
 * the round-trip. Either way the recorder surfaces the backend's honest error,
 * never a faked success.
 *
 * NEXT-FOLLOW-UP DAY (FOLLOWUP-DUE-DATE-PLUMBING-V1; supersedes the OPEN-item
 * note this header used to carry): the grain is now RULED (Deepak 2026-08-21
 * item 8 -- per treatment item, a CUSTOM date) and its carrier (migration
 * 20260826_1) is FOUNDER-APPLIED. The recorder captures the COC's hand-picked
 * day on an item left OPEN; the register and item cards read it back with the
 * ratified urgency form (text + icon: due today = amber clock, past due = red
 * alert). Still NO cadence engine, NO suggested date, NO escalation (the
 * 60-day team-leader threshold is a separate config-owned mechanism, 2026-08-21
 * item 9); register ordering is UNCHANGED -- a lawful STATE order
 * (overdue-first), never a priority score.
 *
 * THE OWNER IN EVERY ROW (COC-FOLLOWUP-OWNERSHIP-SEAT-V1): under a live
 * ownership window (two owners, boundary > 0 -- the Partha org-1 seed) a row
 * the caller may act on now carries ONE quiet State line naming who holds it
 * and the day it moves ("With Front desk until day 30" / "With COC from day
 * 30"); a locked row keeps its existing action-column marker, so the owner
 * is said exactly once per row. Under the seeded default config (one owner)
 * nothing renders and the screen is byte-identical. Display only -- the
 * server's per-row annotation is the truth rendered, never a client rule.
 *
 * Design law: the shipped AdoraCare visual system (VISUAL-CONTRACT-V1;
 * REFERENCE-GRAMMAR-V1). Colour carries ONE job -- severity (amber = overdue
 * attention, per the severity model); ordinary hierarchy is typography +
 * alignment, never more badges. SectionShell for the region headings (no fake
 * headings), row grammar for the register (one action per row), DrawerShell for
 * depth, honest loading/empty/error states, the rupee glyph, IST time. Zero
 * raw button/select/checkbox/input in this file -- every control is a
 * registered ui/ primitive (the next-day picker is ui/DateField.tsx, minted
 * by FOLLOWUP-DUE-DATE-PLUMBING-V1 because no date-field owner existed).
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { safeErrorMessage } from '@/lib/portal-api';
import {
  fetchCocPatientFollowupHistory,
  fetchCocPatientFollowups,
  recordCocFollowupContact,
  type CocFollowupDisposition,
  type CocFollowupHistoryEventView,
  type CocFollowupReason,
  type CocItemFollowupState,
  type PatientFollowupPlanItemView,
  type PatientFollowupQueueView,
  type PatientFollowupRowView,
} from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { useDashboardMeContext } from '@/dashboard/me-context';
import {
  AcBadge,
  AcButton,
  DataTable,
  DateField,
  DrawerShell,
  EmptyState,
  PageHeader,
  PrimaryAction,
  SecondaryAction,
  SectionShell,
  type DataTableColumn,
} from '@/dashboard/ui';
import { formatIstDateTime } from '@/lib/datetime-ist';
import {
  ADVISED_CHIP_LABEL,
  advisedItemsSummary,
  buildContactItems,
  conversionByPatientId,
  conversionStateLabel,
  declinesMissingReason,
  dueDateLabel,
  dueDateTitle,
  dueStateForDate,
  dueUrgencyStep,
  EMPTY_DRAFT,
  filterFollowupRows,
  formatEstimateInr,
  itemDeclineLabel,
  itemDueDate,
  orderRegisterRows,
  reasonLabel,
  REGISTER_ORDER_CLAUSE,
  resolveFollowupLens,
  rowAdvisedItems,
  rowContextLine,
  rowConvertedState,
  rowHasContact,
  rowIsOverdue,
  rowLastContactCell,
  firstFollowupSlaDaysOf,
  rowMayAct,
  rowNextDue,
  rowOwnershipLine,
  rowViewOnlyLabel,
  rowWhereLine,
  selectedItemCount,
  START_HERE_ORDER_CLAUSE,
  startHereRows,
  statesByItemId,
  summarizeFollowups,
  totalEstimateInr,
  treatmentHighlightTone,
  type FollowupFilter,
  type FollowupLens,
  type FollowupSummary,
  type ItemDraft,
  type RowDueInfo,
} from './patient-followups-view';
import { useNarrowViewport } from './use-narrow-viewport';

export type PatientFollowupLoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; status?: number }
  | { kind: 'ready'; view: PatientFollowupQueueView };

export interface PatientFollowupsPageProps {
  /** TEST-ONLY state injection (react-dom/server renders cannot run effects);
   *  production callers render with no props and fetch live. */
  readonly initialState?: PatientFollowupLoadState;
}

/** Compact whole-unit age label -- days-native. */
function formatAgeDays(days: number): string {
  if (days < 1) return 'today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

// ---- Next follow-up due mark (FOLLOWUP-DUE-DATE-PLUMBING-V1) --------------
// The RATIFIED urgency form: row-level text + icon, NEVER a pill/chip
// (R-STATE-URGENCY-PALETTE-01; 02-DESIGN-TOKENS.md 10b.1). Icons mirror the
// COC daily report's page-local pair (CocDailyReportPage.tsx) -- re-authored
// per-page like that page did, since no shared urgency-icon primitive exists
// yet in this codebase. soon = outline clock; breached = filled alert; a
// future day carries NO decoration (absence is the 'none' treatment).

function DueClockIcon(): JSX.Element {
  return (
    <svg className="coc-workboard__due-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 4.5V8l2.5 1.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function DueAlertIcon(): JSX.Element {
  return (
    <svg className="coc-workboard__due-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M8 1.5 15 14H1L8 1.5Z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <rect x="7.25" y="6" width="1.5" height="4" fill="currentColor" />
      <rect x="7.25" y="11" width="1.5" height="1.5" fill="currentColor" />
    </svg>
  );
}

/** The due text + icon for one recorded next-follow-up day. The date text is
 *  ALWAYS printed (colour and icon are never the only carrier); the full
 *  meaning rides `title`/aria-label for hover and assistive readers. */
function DueMark({ due, testId }: { readonly due: RowDueInfo; readonly testId: string }): JSX.Element {
  const step = dueUrgencyStep(due.state);
  return (
    <span
      className={`coc-workboard__due${step !== 'none' ? ` coc-workboard__due--${step}` : ''}`}
      title={dueDateTitle(due)}
      aria-label={dueDateTitle(due)}
      data-testid={testId}
    >
      {step === 'soon' ? <DueClockIcon /> : step === 'breached' ? <DueAlertIcon /> : null}
      {dueDateLabel(due)}
    </span>
  );
}

/** A single-use idempotency key (crypto.randomUUID with the IntakeDrawer
 *  defensive fallback for environments without it). */
function mintRequestId(): string {
  const c = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `coc-followup-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}

/** The in-flight recorder for ONE contact (RULING 6: one contact, many
 *  items): which patient, its open items snapshot, the per-item drafts, and a
 *  single-use whole-contact key. */
interface RecorderState {
  readonly patientId: string;
  readonly patientName: string;
  readonly contextLine: string;
  readonly ageDays: number;
  readonly estimate: number | null;
  readonly items: readonly PatientFollowupPlanItemView[];
  readonly drafts: ReadonlyMap<number, ItemDraft>;
  /** Each item's CURRENT recorded next-follow-up day at open time (FOLLOWUP-
   *  DUE-DATE-PLUMBING-V1): shown per item card, and the draft baseline --
   *  a still-open save re-records the standing day rather than silently
   *  clearing it (the COC clears the input to deliberately un-schedule). */
  readonly currentDueByItem: ReadonlyMap<number, string | null>;
  readonly requestId: string;
  readonly submitting: boolean;
  readonly error: string | null;
}

/** The per-item outcome options -- exactly the #589 vocabulary; '' leaves the
 *  item OPEN (a discussed attempt with no terminal classification). CONVERTED
 *  is deliberately ABSENT (payment-derived, RULING 2). */
const STATE_OPTIONS: ReadonlyArray<{
  readonly value: CocFollowupDisposition | '';
  readonly label: string;
  readonly key: string;
}> = [
  { value: '', label: 'Still open', key: 'open' },
  { value: 'declined', label: 'Declined', key: 'declined' },
  { value: 'unreachable', label: 'Unreachable', key: 'unreachable' },
  { value: 'cold', label: 'Cold', key: 'cold' },
];

/** The SAME per-item outcome vocabulary as STATE_OPTIONS above, looked up
 *  rather than restated (single source of truth) -- a past HISTORY event's
 *  outcome uses the identical words the picker itself offers (the governed
 *  reference's own "Still open" / "Declined" wording,
 *  coc-followups-work-area.html frame 3), never a second copy. */
const STATE_LABEL_BY_VALUE = new Map<CocFollowupDisposition | '', string>(
  STATE_OPTIONS.map((opt) => [opt.value, opt.label]),
);
function historyEventOutcomeLabel(disposition: CocFollowupDisposition | null): string {
  return STATE_LABEL_BY_VALUE.get(disposition ?? '') ?? 'Still open';
}

/** The patient-history fetch for the open drawer (PATIENT-COC-HISTORY-V1) --
 *  a separate load from the register itself; 'idle' while no drawer is open. */
type PatientHistoryLoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; events: readonly CocFollowupHistoryEventView[] };

/** The founder-CONFIRMED non-conversion reasons (DELTA 2026-08-19 E1) -- shown
 *  ONLY under a decline (RULING 3); no invented sub-reasons. */
const REASON_OPTIONS: ReadonlyArray<{
  readonly value: CocFollowupReason;
  readonly label: string;
}> = [
  { value: 'cost', label: 'Cost' },
  { value: 'other', label: 'Other' },
];

/** The register filters -- a quiet utility control at the register heading; the
 *  counters FILTER the list, never stack, and never claim a ranking. Each maps
 *  to a lawful computed state (or the whole set). */
const FILTERS: ReadonlyArray<{
  readonly filter: FollowupFilter;
  readonly label: string;
  readonly key: keyof FollowupSummary;
}> = [
  { filter: 'all', label: 'All', key: 'needAction' },
  { filter: 'overdue', label: 'Overdue', key: 'overdue' },
  { filter: 'newly', label: 'Newly advised', key: 'newlyAdvised' },
  // CSD IS RETIRED (founder ruling 2026-09-04). No CSD chip, filter or
  // column exists anywhere on this screen; `csdHandoffState` is left
  // UNREAD here rather than read and hidden -- a filter nobody can reach
  // is still a filter.
];

export default function PatientFollowupsPage({
  initialState,
}: PatientFollowupsPageProps = {}) {
  const meCtx = useDashboardMeContext();
  const patientMasterEnabled = meCtx?.me?.patientMasterEnabled === true;
  const callerRole = meCtx?.me?.role ?? null;
  // Called with the other hooks, ABOVE the loading/error returns: a hook
  // after a conditional return is a different hook order on the next render.
  const narrow = useNarrowViewport();
  const [state, setState] = useState<PatientFollowupLoadState>(
    initialState ?? { kind: 'loading' },
  );
  const [filter, setFilter] = useState<FollowupFilter>('all');
  const [recorder, setRecorder] = useState<RecorderState | null>(null);
  const [historyState, setHistoryState] = useState<PatientHistoryLoadState>({
    kind: 'idle',
  });

  // PATIENT-COC-HISTORY-V1: the open drawer's FULL ordered history -- a
  // separate fetch from the register (activity 19 is additive, never a
  // widening of fetchCocPatientFollowups). Keyed on the patient id alone so
  // an in-drawer draft edit (a new recorder object on every keystroke) never
  // re-triggers a reload; closing and reopening the SAME patient does (the
  // dependency passes through undefined), which is the desired "fresh on
  // open" behavior.
  const recorderPatientId = recorder?.patientId ?? null;
  useEffect(() => {
    if (recorderPatientId === null) {
      setHistoryState({ kind: 'idle' });
      return;
    }
    let cancelled = false;
    setHistoryState({ kind: 'loading' });
    void fetchCocPatientFollowupHistory(recorderPatientId)
      .then((view) => {
        if (!cancelled) setHistoryState({ kind: 'ready', events: view.events });
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setHistoryState({
            kind: 'error',
            message: safeErrorMessage(e, 'Follow-up details unavailable. Try again.'),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [recorderPatientId]);

  const load = useCallback(() => {
    setState({ kind: 'loading' });
    void fetchCocPatientFollowups()
      .then((view) => setState({ kind: 'ready', view }))
      .catch((e: unknown) => {
        const status =
          typeof e === 'object' && e !== null && 'status' in e
            ? Number((e as { status: unknown }).status)
            : undefined;
        setState({
          kind: 'error',
          message: safeErrorMessage(e, 'Follow-up details unavailable. Try again.'),
          ...(status !== undefined && Number.isFinite(status) ? { status } : {}),
        });
      });
  }, []);

  useEffect(() => {
    if (initialState !== undefined) return;
    load();
  }, [initialState, load]);

  const statesByItem = useMemo<Map<number, CocItemFollowupState>>(
    () =>
      state.kind === 'ready'
        ? statesByItemId(state.view)
        : new Map<number, CocItemFollowupState>(),
    [state],
  );
  const conversions = useMemo<ReturnType<typeof conversionByPatientId>>(
    () =>
      state.kind === 'ready'
        ? conversionByPatientId(state.view)
        : new Map(),
    [state],
  );

  const openRecorder = useCallback(
    (row: PatientFollowupRowView) => {
      const drafts = new Map<number, ItemDraft>();
      const currentDueByItem = new Map<number, string | null>();
      for (const item of row.openItems) {
        const currentDue = itemDueDate(statesByItem.get(item.id));
        currentDueByItem.set(item.id, currentDue);
        drafts.set(item.id, { ...EMPTY_DRAFT, nextFollowupDate: currentDue ?? '' });
      }
      setRecorder({
        patientId: row.patientId,
        patientName: row.patientName,
        contextLine: rowContextLine(row),
        ageDays: row.ageDays,
        estimate: totalEstimateInr(row.openItems),
        items: row.openItems,
        drafts,
        currentDueByItem,
        requestId: mintRequestId(),
        submitting: false,
        error: null,
      });
    },
    [statesByItem],
  );

  const patchDraft = useCallback((itemId: number, next: ItemDraft) => {
    setRecorder((cur) => {
      if (cur === null) return null;
      const drafts = new Map(cur.drafts);
      drafts.set(itemId, next);
      return { ...cur, drafts, error: null };
    });
  }, []);

  const toggleItem = useCallback((itemId: number) => {
    setRecorder((cur) => {
      if (cur === null) return null;
      const drafts = new Map(cur.drafts);
      const prev = drafts.get(itemId) ?? EMPTY_DRAFT;
      // the reset baseline keeps the item's standing next-follow-up day in
      // the input (never a silent un-schedule on a toggle).
      const baseline: ItemDraft = {
        ...EMPTY_DRAFT,
        nextFollowupDate: cur.currentDueByItem.get(itemId) ?? '',
      };
      drafts.set(
        itemId,
        prev.selected ? baseline : { ...baseline, selected: true },
      );
      return { ...cur, drafts, error: null };
    });
  }, []);

  const contactInFlight = useRef(false);
  const submitRecorder = useCallback(() => {
    const cur = recorder;
    if (cur === null || cur.submitting || contactInFlight.current) return;
    const items = buildContactItems(cur.drafts);
    if (items.length === 0) {
      setRecorder({...cur, error: 'Pick at least one treatment you discussed.'});
      return;
    }
    const missing = declinesMissingReason(cur.drafts);
    if (missing.length > 0) {
      const names = missing.map(id => cur.items.find(item => item.id === id)?.procedureName)
        .filter((name): name is string => name !== undefined);
      setRecorder({...cur, error: names.length > 0
        ? `Say why it was declined: ${names.join(', ')}.` : 'Say why it was declined.'});
      return;
    }
    // A network side effect must never run inside a React state updater:
    // updater replay would send twice and release pending controls too early.
    const {patientId, requestId} = cur;
    contactInFlight.current = true;
    setRecorder({...cur, submitting: true, error: null});
    void recordCocFollowupContact({patientId, items, requestId})
      .then(() => {
        setRecorder(current => current?.requestId === requestId ? null : current);
        load();
      })
      .catch((error: unknown) => {
        setRecorder(current => current?.requestId === requestId ? {...current,
          submitting: false,
          error: safeErrorMessage(error, 'Contact could not be saved. Please try again.'),
        } : current);
      })
      .finally(() => { contactInFlight.current = false; });
  }, [recorder, load]);

  if (state.kind === 'loading') {
    return (
      <section className="coc-workboard" data-testid="patient-followups-loading">
        <div className="coc-workboard__loading">Loading your follow-up work...</div>
      </section>
    );
  }
  if (state.kind === 'error') {
    return (
      <section className="coc-workboard" data-testid="patient-followups-error">
        <DashboardLoadError
          message={state.message}
          {...(state.status !== undefined ? { status: state.status } : {})}
        />
      </section>
    );
  }

  const { view } = state;
  const summary = summarizeFollowups(view);
  // FIRST-FOLLOWUP-SLA-KEY-V1: the org's SLA, read ONCE off the wire and
  // passed to every overdue derivation on this pass, so the band, the
  // ordering, the filter and each row badge cannot disagree.
  const slaDays = firstFollowupSlaDaysOf(view);
  // WHICH LENS: mirrors the server's own relation ordering off the wire's
  // config block (resolveFollowupLens). Never the fence -- the write model
  // refuses the POST on the same derivation regardless.
  const lens = resolveFollowupLens(callerRole, view.followupOwnership);
  const ordered = orderRegisterRows(view.rows, statesByItem, slaDays);
  const rows = filterFollowupRows(ordered, filter, statesByItem, slaDays);
  const nothingWaiting = view.rows.length === 0;
  const startHere = startHereRows(view.rows, statesByItem, slaDays);
  // ONE render clock for every due-standing derivation on this pass
  // (FOLLOWUP-DUE-DATE-PLUMBING-V1) -- the pure helpers take it explicitly.
  const nowMs = Date.now();

  return (
    <section
      className="coc-workboard"
      data-testid="patient-followups-page"
      aria-label="Patient follow-ups"
    >
      <PageHeader
        title="Follow-ups"
        subs={[
          /* ONE LINE FOR THE READER IT HAS (corrective 2 item 4). "follow-up
             WORK only" is the COC's sentence -- it describes a queue you act
             on. The read-only lens acts on nothing, so under a VIEW ONLY
             region that line contradicted the screen it sat above. */
          lens === 'read_only'
            ? 'Your assigned clinics - follow-up status, view only'
            : 'Your assigned clinics - follow-up work only',
          `As of ${formatIstDateTime(view.generatedAtIso)}`,
        ]}
        testId="patient-followups-header"
      />

      <StartHereRegion
        cases={startHere}
        overdueTotal={summary.overdue}
        lens={lens}
        patientMasterEnabled={patientMasterEnabled}
        ownershipConfig={view.followupOwnership}
        onCall={openRecorder}
      />

      {nothingWaiting ? (
        <EmptyState
          title="Nothing waiting"
          body="No patient needs a follow-up right now."
          testId="patient-followups-empty"
        />
      ) : (
        <SectionShell
          title="All open follow-ups"
          sub={
            <>
              {view.rows.length} {view.rows.length === 1 ? 'patient' : 'patients'} ·{' '}
              {REGISTER_ORDER_CLAUSE.toLowerCase()}
              {view.capped ? ' · longest-waiting shown, count is a lower bound' : ''}
            </>
          }
          actions={
            <div
              className="coc-workboard__filters"
              role="group"
              aria-label="Filter the register"
              data-testid="coc-workboard-filters"
            >
              {FILTERS.map((f) => (
                <AcButton
                  key={f.filter}
                  variant={filter === f.filter ? 'secondary' : 'ghost'}
                  size="sm"
                  aria-pressed={filter === f.filter}
                  onClick={() =>
                    setFilter((cur) => (cur === f.filter ? 'all' : f.filter))
                  }
                  data-testid={`coc-workboard-filter-${f.filter}`}
                >
                  {f.label}
                  <span className="coc-workboard__tab-count">{summary[f.key]}</span>
                </AcButton>
              ))}
            </div>
          }
          testId="coc-workboard-register"
        >
          <DataTable<PatientFollowupRowView>
            testId="coc-workboard-queue"
            label={lens === 'read_only' ? 'Follow-up status' : 'Follow-up register'}
            columns={registerColumns({
              lens,
              narrow,
              patientMasterEnabled,
              statesByItem,
              conversions,
              slaDays,
              nowMs,
              ownershipConfig: view.followupOwnership,
              onFollowUp: openRecorder,
            })}
            rows={rows}
            rowKey={(row) => row.patientId}
            rowTestId={(row) => `coc-followup-row-${row.patientId}`}
            {...(narrow && lens !== 'read_only'
              ? {
                  /* AT THE TABLET FOLD the row IS the affordance, because the
                     action column is not there (registerColumns says why).
                     NEVER on the read-only lens: it opens nothing, and a row
                     that looks tappable and does nothing is a worse lie than
                     a missing button. NEVER on a row the caller may not act
                     on -- the server would refuse the write anyway, and the
                     row already names its owner. */
                  onRowClick: (row: PatientFollowupRowView) => {
                    if (rowMayAct(row)) openRecorder(row);
                  },
                }
              : {})}
            rowClassName={(row) =>
              rowHasContact(row, statesByItem)
                ? 'coc-workboard__row--contacted'
                : undefined
            }
            countLine={
              <span data-testid="coc-workboard-count">
                Showing {rows.length}
                {filter !== 'all' ? ` of ${view.rows.length}` : ''}
              </span>
            }
            empty={
              <EmptyState
                title="Nothing in this view"
                body="No patient matches this filter right now."
                testId="patient-followups-filter-empty"
              />
            }
          />
        </SectionShell>
      )}

      {recorder !== null ? (
        <ContactDrawer
          recorder={recorder}
          historyState={historyState}
          onClose={() => setRecorder(null)}
          onToggleItem={toggleItem}
          onPatchDraft={patchDraft}
          onSubmit={submitRecorder}
        />
      ) : null}
    </section>
  );
}

/** THE START HERE LENS (approved reference, artboard 1): the attention region
 *  is the SYSTEM ORGANIZING the work -- the overdue-and-nobody-has-called-yet
 *  cases (a lawful computed STATE, never a ranking score), LONGEST WAITING
 *  FIRST (the order stated on the glass), showing up to three. Each is a real
 *  case with WHO + estimate + context + how long it has waited, and ONE primary
 *  action ("Call now"). A VIEW over the register -- these patients REMAIN in the
 *  full register below (DUPLICATION LAW). Nothing overdue is a calm caught-up
 *  line, never a fake zero. */
function StartHereRegion({
  cases,
  overdueTotal,
  lens,
  patientMasterEnabled,
  ownershipConfig,
  onCall,
}: {
  readonly cases: readonly PatientFollowupRowView[];
  readonly overdueTotal: number;
  readonly lens: FollowupLens;
  readonly patientMasterEnabled: boolean;
  readonly ownershipConfig: PatientFollowupQueueView['followupOwnership'];
  readonly onCall: (row: PatientFollowupRowView) => void;
}): JSX.Element {
  const hasCases = cases.length > 0;
  const sub = hasCases
    ? `${overdueTotal} ${overdueTotal === 1 ? 'patient' : 'patients'} · ${START_HERE_ORDER_CLAUSE}${
        cases.length < overdueTotal ? ` · showing ${cases.length}` : ''
      }`
    : undefined;
  return (
    <SectionShell
      /* "Start here" is an instruction to ACT, and the read-only lens cannot
         act by design. Its marker is not invented here: it is the shipped
         view-only wording (rowViewOnlyLabel), said once for the region
         instead of once per row. */
      eyebrow={lens === 'read_only' ? 'With the COC - view only' : 'Start here'}
      title={hasCases ? 'Overdue, nobody has called yet' : "You're all caught up"}
      {...(sub !== undefined ? { sub } : {})}
      tint="teal"
      testId="coc-workboard-summary"
    >
      {hasCases ? (
        <ul className="coc-workboard__starthere" data-testid="coc-workboard-starthere">
          {cases.map((row) => (
            <StartHereCase
              key={row.patientId}
              row={row}
              lens={lens}
              ownerLine={rowOwnershipLine(row, ownershipConfig)}
              patientMasterEnabled={patientMasterEnabled}
              onCall={() => onCall(row)}
            />
          ))}
        </ul>
      ) : (
        <p className="coc-workboard__caughtup" data-testid="coc-workboard-caughtup">
          No follow-up is overdue right now - nobody is waiting past the first
          follow-up.
        </p>
      )}
    </SectionShell>
  );
}

/** One START HERE case: WHO + estimate + the full treatment/where context + how
 *  long it has waited ("N days, never contacted", the only coloured element),
 *  and the primary "Call now" action that opens the same contact drawer. */
function StartHereCase({
  row,
  lens,
  ownerLine,
  patientMasterEnabled,
  onCall,
}: {
  readonly row: PatientFollowupRowView;
  readonly lens: FollowupLens;
  /** The one ownership line (rowOwnershipLine); null renders nothing. */
  readonly ownerLine: string | null;
  readonly patientMasterEnabled: boolean;
  readonly onCall: () => void;
}): JSX.Element {
  const estimate = totalEstimateInr(row.openItems);
  return (
    <li className="coc-workboard__sh" data-testid={`coc-followup-starthere-${row.patientId}`}>
      <div className="coc-workboard__sh-main">
        <span className="coc-workboard__sh-name">
          {patientMasterEnabled ? (
            <Link
              to={`/dashboard/patients/${row.patientId}`}
              className="coc-workboard__name-link"
              data-testid={`coc-followup-patient-link-${row.patientId}`}
            >
              {row.patientName}
            </Link>
          ) : (
            row.patientName
          )}
        </span>
        <span className="coc-workboard__sh-money">{formatEstimateInr(estimate)}</span>
        <span className="coc-workboard__sh-context">{rowContextLine(row)}</span>
        <span className="coc-workboard__sh-wait">
          {formatAgeDays(row.ageDays)}, never contacted
        </span>
        {ownerLine !== null ? (
          <span
            className="coc-workboard__sh-owner"
            data-testid={`coc-followup-owner-sh-${row.patientId}`}
          >
            {ownerLine}
          </span>
        ) : null}
      </div>
      {/* The read-only lens carries NO write control at all -- not a
          disabled one. The region's own eyebrow already says whose it is,
          so the per-case marker would be the same sentence twice. */}
      {lens === 'read_only' ? null : rowMayAct(row) ? (
        <PrimaryAction
          onClick={onCall}
          data-testid={`coc-followup-callnow-${row.patientId}`}
        >
          Call now
        </PrimaryAction>
      ) : (
        <span
          className="coc-workboard__last-meta"
          data-testid={`coc-followup-viewonly-sh-${row.patientId}`}
        >
          {rowViewOnlyLabel(row)}
        </span>
      )}
    </li>
  );
}

/* ---------------------------------------------------------------------- *
 *  THE REGISTER, AS ONE TABLE (Phase 2 Slice 1, R5 frames 01 / 07-09)
 *
 *  WHAT CHANGED AND WHY. The register was a hand-rolled CSS grid -- its own
 *  `__colhead` + `__row` classes, its own six-column track, its own tablet
 *  media rule -- which is exactly the "width map that could not be reused"
 *  COLUMN-WIDTHS-V1 was authorised to end (#760, condition A). It is now THE
 *  shared `DataTable`, with the widths declared ON the column definitions
 *  where the caller already declares everything else about a column. No
 *  primitive is forked and no lookalike is hand-drawn.
 *
 *  THE CELLS KEEP THE SHIPPED GRAMMAR. Every leaf class below
 *  (`__name`, `__treatments`, `__advised`, `__where`, `__estimate`, `__age`,
 *  `__last-outcome`, `__owner`, the due mark) is the register's OWN existing
 *  typography, reused verbatim inside a `<td>`. The `grid-area` declarations
 *  those classes also carry are inert outside a grid container, so this file
 *  needs no CSS change at all -- and adds no media rule.
 *
 *  COLOUR STILL HAS ONE JOB: amber = overdue attention. The chips are the
 *  SHIPPED ones (AcBadge pending / the canonical Converted tone), not the
 *  preview's StatusChip pairing -- the real application's severity model
 *  wins over a preview measurement, and no founder ruling re-coloured
 *  Overdue.
 * ---------------------------------------------------------------------- */

/** WHO: the patient identity, with clinic and doctor STACKED beneath it --
 *  the approved plate-1 arrangement, now expressible because
 *  `.ac-cell-stack` (#760, condition A) opts this one cell out of the
 *  table's `white-space: nowrap`. */
function PatientIdentityCell({
  row,
  patientMasterEnabled,
}: {
  readonly row: PatientFollowupRowView;
  readonly patientMasterEnabled: boolean;
}): JSX.Element {
  const where = rowWhereLine(row);
  return (
    <span className="ac-cell-stack">
      <span className="coc-workboard__name">
        {patientMasterEnabled ? (
          <Link
            to={`/dashboard/patients/${row.patientId}`}
            className="coc-workboard__name-link"
            data-testid={`coc-followup-patient-link-${row.patientId}`}
          >
            {row.patientName}
          </Link>
        ) : (
          <span>{row.patientName}</span>
        )}
      </span>
      {where !== '' ? (
        <span className="ac-cell-stack__meta coc-workboard__where">{where}</span>
      ) : null}
    </span>
  );
}

/** TREATMENT: the FULL open scope -- every name, never "+1 more" (#593 item
 *  8) -- each carrying the doctor's authoritative decline inline where one
 *  exists, plus the read-only ADVISED block. Unchanged from the shipped row;
 *  only its container moved. */
function TreatmentsCell({ row }: { readonly row: PatientFollowupRowView }): JSX.Element {
  const advisedItems = rowAdvisedItems(row);
  return (
    <span className="ac-cell-stack">
      {row.openItems.length > 0 ? (
        <span className="coc-workboard__treatments">
          {row.openItems.map((item, i) => {
            const declineLabel = itemDeclineLabel(item);
            const separator = i < row.openItems.length - 1 ? ', ' : '';
            // Plain text, byte-identical to the pre-#16 join, UNLESS this
            // exact item carries the doctor's authoritative decline (then,
            // and only then, an inline marker rides beside its name --
            // never collapsed into the page's OWN "Last contact" column).
            return declineLabel === null ? (
              <Fragment key={item.id}>
                {item.procedureName}
                {separator}
              </Fragment>
            ) : (
              <Fragment key={item.id}>
                {item.procedureName}
                <span
                  className="coc-workboard__item-decline"
                  data-testid={`coc-followup-item-decline-${item.id}`}
                >
                  {' '}
                  ({declineLabel})
                </span>
                {separator}
              </Fragment>
            );
          })}
        </span>
      ) : null}
      {advisedItems.length > 0 ? (
        <span
          className="coc-workboard__advised"
          data-testid={`coc-followup-advised-${row.patientId}`}
        >
          <AcBadge tone="info" testId={`coc-followup-advised-badge-${row.patientId}`}>
            {ADVISED_CHIP_LABEL}
          </AcBadge>
          <span className="coc-workboard__advised-names">
            {advisedItemsSummary(advisedItems)}
          </span>
        </span>
      ) : null}
    </span>
  );
}

/** STATE: the chips only -- amber Overdue and the canonical Converted fact,
 *  side by side because they answer different questions -- then the one
 *  quiet ownership line. NO CSD CHIP (founder ruling 2026-09-04). The next
 *  follow-up day is NOT here any more: it has its own NEXT column (R5 item
 *  4), still in the ratified text+icon form, never inside a chip. */
function RegisterStateCell({
  row,
  overdue,
  convertedState,
  ownerLine,
}: {
  readonly row: PatientFollowupRowView;
  readonly overdue: boolean;
  readonly convertedState: ReturnType<typeof rowConvertedState>;
  readonly ownerLine: string | null;
}): JSX.Element {
  return (
    <span className="coc-workboard__cell coc-workboard__cell--state">
      {overdue ? (
        <AcBadge tone="pending" testId={`coc-followup-overdue-${row.patientId}`}>
          Overdue
        </AcBadge>
      ) : null}
      {convertedState !== null ? (
        <AcBadge
          tone={treatmentHighlightTone(convertedState)}
          title={conversionStateLabel(convertedState)}
          testId={`coc-followup-converted-${row.patientId}`}
        >
          Converted
        </AcBadge>
      ) : null}
      {ownerLine !== null ? (
        <span
          className="coc-workboard__owner"
          data-testid={`coc-followup-owner-${row.patientId}`}
        >
          {ownerLine}
        </span>
      ) : null}
    </span>
  );
}

/** LAST CONTACT (restored, R5 item 4): a DASH until the row is contacted,
 *  then the outcome + the supervisor-safe named actor. ONE construction, so
 *  the fact carries one name on every viewport. */
function LastContactCell({
  cell,
}: {
  readonly cell: ReturnType<typeof rowLastContactCell>;
}): JSX.Element {
  if (cell === null) {
    return (
      <span className="coc-workboard__last-none" aria-label="Not contacted yet">
        —
      </span>
    );
  }
  /* STACKED, not truncated. `.ac-table td` is `white-space: nowrap` with
     hidden overflow, so on one line "Declined · Cost — Priya · 7 Sep" needed
     217px against the 166px the column had and was silently cut mid-word --
     a width that REMOVES information, which is the same defect as removing it
     in code. It is two facts anyway (what happened; who and when), so it
     takes the shared `.ac-cell-stack` that the identity cell uses, which
     opts the cell out of nowrap. Measured by the frame shooter, not judged
     by eye. */
  return (
    <span className="ac-cell-stack coc-workboard__cell--last">
      <span
        className={`coc-workboard__last-outcome${
          cell.declined ? ' coc-workboard__last-outcome--declined' : ''
        }`}
      >
        {cell.outcome}
      </span>
      <span className="ac-cell-stack__meta coc-workboard__last-meta">
        {cell.meta}
      </span>
    </span>
  );
}

/**
 * THE COLUMN SET, AND WHERE ITS WIDTHS COME FROM.
 *
 * The percentages are R5's, measured against the register's real width at
 * 1440 and at the tablet fold -- not preferences. `[data-visual='v1']
 * .ac-table` is `table-layout: fixed`, so with no widths every column takes
 * an identical share and the identity cell starves while WAITING is over-fed.
 *
 * WHAT FOLDS AT THE TABLET FOLD, AND WHAT NEVER DOES. Only TREATMENT and
 * ESTIMATE fold, and both are one tap away in the contact record. NEXT stays
 * in the list on both viewports, because NEXT is how the COC decides whom to
 * contact next; LAST CONTACT stays for the same reason. Nothing folds
 * silently -- the register's own sub-line says the order it is in, and the
 * record carries what the list dropped.
 *
 * THE READ-ONLY LENS CARRIES NO ACTION COLUMN AT ALL. Not a disabled button,
 * not a "view only" marker in an action slot: the column does not exist, and
 * the width it would have taken is given to the identity cell. A lens that
 * cannot act should not spend a column saying so.
 */
export function registerColumns({
  lens,
  narrow,
  patientMasterEnabled,
  statesByItem,
  conversions,
  slaDays,
  nowMs,
  ownershipConfig,
  onFollowUp,
}: {
  readonly lens: FollowupLens;
  readonly narrow: boolean;
  readonly patientMasterEnabled: boolean;
  readonly statesByItem: Map<number, CocItemFollowupState>;
  readonly conversions: ReturnType<typeof conversionByPatientId>;
  readonly slaDays: number | null;
  readonly nowMs: number;
  readonly ownershipConfig: PatientFollowupQueueView['followupOwnership'];
  readonly onFollowUp: (row: PatientFollowupRowView) => void;
}): DataTableColumn<PatientFollowupRowView>[] {
  const readOnly = lens === 'read_only';
  const cols: DataTableColumn<PatientFollowupRowView>[] = [
    {
      key: 'who',
      header: 'Patient',
      render: (row) => (
        <PatientIdentityCell row={row} patientMasterEnabled={patientMasterEnabled} />
      ),
      // DESKTOP is a PERCENTAGE allocation summing to 100%: the table fills
      // its container and every column gets the share its content measured.
      // The action column's share goes to the identity cell on the read-only
      // lens, which holds the longest content on the screen.
      //
      // THE TABLET FOLD IS ABSOLUTE (rem), AND THAT IS THE POINT. Measured at
      // 834 the columns the founder requires -- money in the row (R4), one
      // name per fact, LAST CONTACT and NEXT kept -- need about 102% of the
      // 744px the register has. As percentages that is not a layout, it is a
      // squeeze: `table-layout: fixed` shrinks every cell to its share and the
      // longest surname, the Converted badge and the "Follow up" button all
      // truncate. Absolute widths let the table be its honest width inside
      // `.ac-table-scroll`, the shared primitive's own overflow-x container --
      // "the table scrolls inside its own container" is DataTable's stated
      // mobile posture, not a workaround. So NOTHING is dropped to make the
      // arithmetic work: the register scrolls a little instead of lying a lot.
      width: narrow ? (readOnly ? '7.75rem' : '9.5rem') : readOnly ? '30.0%' : '19.0%',
    },
  ];
  /* TREATMENT -- desktop always; at the tablet fold, the READ-ONLY lens only.
     Corrective 2 item 1: the read-only lens has no action column, so it has
     the room, and without it Treatment and Estimate became UNREACHABLE for a
     front desk that has no drawer to open them in. The work lens spends that
     width on its action instead and MOVES Treatment to the record -- declared
     in the run record, one tap away, never silently dropped. */
  if (!narrow || readOnly) {
    cols.push({
      key: 'trt',
      header: 'Treatment',
      render: (row) => <TreatmentsCell row={row} />,
      width: narrow ? '6.75rem' : '11.4%',
    });
  }
  /* MONEY STAYS IN THE ROW at every width -- R4 ruling, corrective 2 item 3.
     It used to fold at the tablet fold, which is exactly what that ruling
     forbids. No condition: this column exists on both lenses and both
     viewports. */
  cols.push({
    key: 'est',
    header: 'Estimate',
    align: 'end',
    render: (row) => (
      <span className="coc-workboard__estimate">
        {formatEstimateInr(totalEstimateInr(row.openItems))}
      </span>
    ),
    width: narrow ? (readOnly ? '6.25rem' : '7rem') : '8.6%',
  });
  cols.push({
    key: 'state',
    header: 'State',
    render: (row) => (
      <RegisterStateCell
        row={row}
        overdue={rowIsOverdue(row, statesByItem, slaDays)}
        convertedState={rowConvertedState(row, conversions)}
        ownerLine={rowOwnershipLine(row, ownershipConfig)}
      />
    ),
    width: narrow ? (readOnly ? '7.25rem' : '7.5rem') : '16.0%',
  });
  cols.push({
    key: 'wait',
    /* ONE NAME PER FACT ACROSS BREAKPOINTS (corrective 2 item 3). "Wait" and
       "Last" were shortenings that read as different columns; the fact is the
       same fact at 834 as at 1440, so it carries the same name. */
    header: 'Waiting',
    align: 'end',
    render: (row) => (
      <span className="coc-workboard__age">{formatAgeDays(row.ageDays)}</span>
    ),
    width: narrow ? (readOnly ? '5rem' : '5.5rem') : '7.0%',
  });
  cols.push({
    key: 'last',
    header: 'Last contact',
    render: (row) => <LastContactCell cell={rowLastContactCell(row, statesByItem)} />,
    width: narrow ? (readOnly ? '7.5rem' : '8rem') : '14.0%',
  });
  cols.push({
    key: 'next',
    header: 'Next',
    render: (row) => {
      const due = rowNextDue(row, statesByItem, nowMs);
      return due === null ? (
        <span className="coc-workboard__last-none" aria-label="No next follow-up set">
          —
        </span>
      ) : (
        <DueMark due={due} testId={`coc-followup-due-${row.patientId}`} />
      );
    },
    width: narrow ? (readOnly ? '7.5rem' : '8.5rem') : '13.0%',
  });
  // THE READ-ONLY LENS ENDS HERE -- no action column exists on it.
  if (readOnly) return cols;
  /* AND AT THE TABLET FOLD THE WORK LENS HAS NO ACTION COLUMN EITHER -- the
     ROW opens the drawer instead (DataTable's own `onRowClick`: "makes the
     whole row the drill affordance (focusable, Enter activates)").
     WHY, MEASURED: with the action column the seven tablet columns need 772px
     against the 744px the register has, so the table scrolled and the button
     -- the primary action -- was the part left off-screen. A primary action
     you have to scroll sideways to reach is worse than no button at all. Drop
     the column and the remaining six fit exactly, with every fact the founder
     required still in the row: money (R4), Waiting, Last contact and Next,
     each under its full name. Nothing is folded to buy this. */
  if (narrow) return cols;
  cols.push({
    key: 'act',
    header: '',
    render: (row) =>
      rowMayAct(row) ? (
        <SecondaryAction
          size="sm"
          onClick={() => onFollowUp(row)}
          data-testid={`coc-followup-followup-${row.patientId}`}
        >
          Follow up
        </SecondaryAction>
      ) : (
        /* FOLLOWUP-OWNERSHIP-ROUTING-CONFIG-V1 (R2): a case the caller
           may not act on renders WHO owns it instead of the action -- a
           DISPLAY flag only; the server refuses the write regardless. */
        <span
          className="coc-workboard__last-meta"
          data-testid={`coc-followup-viewonly-${row.patientId}`}
        >
          {rowViewOnlyLabel(row)}
        </span>
      ),
    width: narrow ? '7.5rem' : '11.0%',
  });
  return cols;
}

/** The focused contact drawer: pick the treatments discussed in one call, set
 *  each outcome, Cost/Other only under a decline, save once. Recent activity
 *  proves persistence + the named actor on reload. */
function ContactDrawer({
  recorder,
  historyState,
  onClose,
  onToggleItem,
  onPatchDraft,
  onSubmit,
}: {
  readonly recorder: RecorderState;
  readonly historyState: PatientHistoryLoadState;
  readonly onClose: () => void;
  readonly onToggleItem: (itemId: number) => void;
  readonly onPatchDraft: (itemId: number, next: ItemDraft) => void;
  readonly onSubmit: () => void;
}): JSX.Element {
  const selected = selectedItemCount(recorder.drafts);
  // ONE clock for the per-item due-standing marks in this drawer render.
  const nowMs = Date.now();

  return (
    <DrawerShell
      eyebrow="Follow-up contact"
      title={recorder.patientName}
      onClose={onClose}
      closeDisabled={recorder.submitting}
      closeOnScrim={!recorder.submitting}
      closeOnEscape={!recorder.submitting}
      testId="coc-followup-drawer"
      footer={
        <div className="coc-workboard__drawer-footer">
          <div className="coc-workboard__drawer-footline">
            <span className="coc-workboard__drawer-footnote">
              Records one contact · {selected} {selected === 1 ? 'treatment' : 'treatments'}
            </span>
            <PrimaryAction
              onClick={onSubmit}
              disabled={recorder.submitting || selected === 0}
              data-testid="coc-followup-save"
            >
              {recorder.submitting
                ? 'Saving...'
                : selected > 0
                  ? `Save contact (${selected})`
                  : 'Save contact'}
            </PrimaryAction>
          </div>
          {recorder.error !== null ? (
            <span
              className="coc-workboard__drawer-error"
              role="alert"
              data-testid="coc-followup-drawer-error"
            >
              {recorder.error}
            </span>
          ) : null}
        </div>
      }
    >
      <p className="coc-workboard__drawer-context">{recorder.contextLine}</p>
      <div className="coc-workboard__drawer-meta">
        <span className="coc-workboard__drawer-money">
          {formatEstimateInr(recorder.estimate)}
        </span>
        <span className="coc-workboard__drawer-dot" aria-hidden="true" />
        <span className="coc-workboard__drawer-wait">
          waiting {formatAgeDays(recorder.ageDays)}
        </span>
      </div>

      <section className="coc-workboard__drawer-section">
        <div className="coc-workboard__drawer-head">
          <h3 className="coc-workboard__drawer-heading">Treatments discussed</h3>
          <span className="coc-workboard__drawer-count">
            {selected} of {recorder.items.length} selected
          </span>
        </div>
        <p className="coc-workboard__drawer-hint">
          Pick what you covered in this one call, and set how each ended.
        </p>
        <ul className="coc-workboard__recorder">
          {recorder.items.map((item) => {
            const draft = recorder.drafts.get(item.id) ?? EMPTY_DRAFT;
            const currentDue = recorder.currentDueByItem.get(item.id) ?? null;
            return (
              <li
                key={item.id}
                className={`coc-workboard__item-card${
                  draft.selected ? ' coc-workboard__item-card--selected' : ''
                }`}
              >
                <AcButton
                  className="coc-workboard__item-head"
                  aria-pressed={draft.selected}
                  disabled={recorder.submitting}
                  onClick={() => onToggleItem(item.id)}
                  data-testid={`coc-followup-item-toggle-${item.id}`}
                >
                  <span className="coc-workboard__item-check" aria-hidden="true">
                    {draft.selected ? (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    ) : null}
                  </span>
                  <span className="coc-workboard__item-name">{item.procedureName}</span>
                  <span className="coc-workboard__item-money num">
                    {formatEstimateInr(item.estimatedValue)}
                  </span>
                </AcButton>

                {/* This item's standing next follow-up day (per-item grain,
                    ruling 2026-08-21 item 8) -- same due mark as the register. */}
                {currentDue !== null ? (
                  <div className="coc-workboard__item-due">
                    <DueMark
                      due={{ dueDate: currentDue, state: dueStateForDate(currentDue, nowMs) }}
                      testId={`coc-followup-item-due-${item.id}`}
                    />
                  </div>
                ) : null}

                {draft.selected ? (
                  <div className="coc-workboard__item-body">
                    <p className="coc-workboard__disposition-label">How did it end?</p>
                    <div
                      className="coc-workboard__states"
                      role="group"
                      aria-label={`Outcome for ${item.procedureName}`}
                    >
                      {STATE_OPTIONS.map((opt) => (
                        <AcButton
                          key={opt.key}
                          variant={draft.disposition === opt.value ? 'primary' : 'ghost'}
                          size="sm"
                          aria-pressed={draft.disposition === opt.value}
                          disabled={recorder.submitting}
                          onClick={() =>
                            onPatchDraft(item.id, {
                              ...draft,
                              disposition: opt.value,
                              reason: opt.value === 'declined' ? draft.reason : '',
                              // a next day rides ONLY an item left OPEN (grain
                              // ruling item 8) -- a terminal pick clears it.
                              nextFollowupDate:
                                opt.value === '' ? draft.nextFollowupDate : '',
                            })
                          }
                          data-testid={`coc-followup-item-state-${item.id}-${opt.key}`}
                        >
                          {opt.label}
                        </AcButton>
                      ))}
                    </div>

                    {draft.disposition === '' ? (
                      /* FOLLOWUP-DUE-DATE-PLUMBING-V1: the hand-picked next
                         follow-up day -- offered only while the item stays
                         OPEN, through the registered DateField primitive
                         (plain label, no format hint -- the R-41/F9 one
                         date-input pattern). Empty = none scheduled. */
                      <div className="coc-workboard__nextdate-row">
                        <DateField
                          id={`coc-followup-nextdate-${item.id}`}
                          label="Next follow-up date"
                          value={draft.nextFollowupDate}
                          disabled={recorder.submitting}
                          onChange={(value) =>
                            onPatchDraft(item.id, {
                              ...draft,
                              nextFollowupDate: value,
                            })
                          }
                          testId={`coc-followup-item-nextdate-${item.id}`}
                        />
                      </div>
                    ) : null}

                    {draft.disposition === 'declined' ? (
                      <div className="coc-workboard__reasons-row">
                        <p className="coc-workboard__disposition-label">Why?</p>
                        <div
                          className="coc-workboard__reasons"
                          role="group"
                          aria-label={`Decline reason for ${item.procedureName}`}
                        >
                          {REASON_OPTIONS.map((opt) => (
                            <AcButton
                              key={opt.value}
                              variant={draft.reason === opt.value ? 'secondary' : 'ghost'}
                              size="sm"
                              aria-pressed={draft.reason === opt.value}
                              disabled={recorder.submitting}
                              onClick={() =>
                                onPatchDraft(item.id, { ...draft, reason: opt.value })
                              }
                              data-testid={`coc-followup-item-reason-${item.id}-${opt.value}`}
                            >
                              {opt.label}
                            </AcButton>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="coc-workboard__drawer-section">
        <h3 className="coc-workboard__drawer-heading">Earlier contacts</h3>
        {historyState.kind === 'loading' || historyState.kind === 'idle' ? (
          <p
            className="coc-workboard__drawer-hint"
            data-testid="coc-followup-history-loading"
          >
            Loading history...
          </p>
        ) : historyState.kind === 'error' ? (
          <span
            className="coc-workboard__drawer-error"
            role="alert"
            data-testid="coc-followup-history-error"
          >
            {historyState.message}
          </span>
        ) : historyState.events.length === 0 ? (
          <p
            className="coc-workboard__drawer-empty"
            data-testid="coc-followup-history-empty"
          >
            No follow-ups recorded yet.
          </p>
        ) : (
          // FULL ORDERED history, newest-first (activity 19): one row per
          // recorded event, never collapsed to one row per item -- the same
          // item can carry several rows (coc-followups-work-area.html
          // frame 3, the Aug-20 decline above the Aug-12 still-open row).
          <ul className="coc-workboard__timeline" data-testid="coc-followup-history">
            {historyState.events.map((event) => {
              const declinedEntry = event.disposition === 'declined';
              return (
                <li key={event.id} className="coc-workboard__timeline-row">
                  <span
                    className={`coc-workboard__timeline-dot${
                      declinedEntry ? ' coc-workboard__timeline-dot--declined' : ''
                    }`}
                    aria-hidden="true"
                  />
                  <div className="coc-workboard__timeline-body">
                    <div
                      className={`coc-workboard__timeline-state${
                        declinedEntry ? ' coc-workboard__timeline-state--declined' : ''
                      }`}
                    >
                      {historyEventOutcomeLabel(event.disposition)}
                      {event.reason !== null ? ` · ${reasonLabel(event.reason)}` : ''}
                      {event.nextFollowupDueDate ? ` · Next follow-up ${event.nextFollowupDueDate}` : ''}
                    </div>
                    <div className="coc-workboard__timeline-item">
                      {event.procedureName ?? 'Treatment'}
                    </div>
                    <div className="coc-workboard__timeline-when">
                      {event.actorLabel ?? event.actorRole}
                      {` · ${formatIstDateTime(event.createdAtIso)}`}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </DrawerShell>
  );
}
